import { colors, white } from '@kmns-internal/furikake/tokens'
import { InfoOutline } from '@mui/icons-material'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import type { PlaceNode, User, UserGroup } from '@ulysses-inc/harami_api_client'
import { PlaceNodeTypeEnum } from '@ulysses-inc/harami_api_client'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import usersOperations from 'src/state/ducks/users/operations'
import type { RootState } from 'src/state/store'
import { roleLabels } from './roleOptions'

const formatPlaceNodeNames = (
  userPlaceNodes: PlaceNode[] | undefined,
  allPlaceNodes: PlaceNode[],
) => {
  return userPlaceNodes && userPlaceNodes.length > 0
    ? userPlaceNodes
        .map(node => {
          if (node.type === PlaceNodeTypeEnum.Place) {
            return (
              allPlaceNodes.find(
                placeNode => placeNode.place?.placeNodeId === node.id,
              )?.place?.name || ''
            )
          } else {
            return (
              allPlaceNodes.find(
                placeNode => placeNode.placeGroup?.placeNodeId === node.id,
              )?.placeGroup?.name || ''
            )
          }
        })
        .join(' ')
    : ''
}

const formatUserGroupNames = (userGroups: UserGroup[] | undefined) => {
  return userGroups && userGroups.length > 0
    ? userGroups.map((userGroup: UserGroup) => userGroup.name).join(' ')
    : ''
}

interface OwnProps {
  users: User[]
}

const UserList: React.FC<OwnProps> = (props: OwnProps) => {
  const dispatch = useDispatch()

  const {
    placeNodes: { nodes },
  } = useSelector((state: RootState) => state.placesState)

  const openEditUser = (user: User) => {
    usersOperations.updateActiveUserId(dispatch, user.uuid ?? '')
    usersOperations.changeIsShowEditUserDrawer(dispatch, true)
  }

  return (
    <TableContainer
      sx={{
        overflow: 'auto',
        position: 'relative',
        flexGrow: 1,
        bgcolor: white,
      }}
    >
      <Table sx={{ bgcolor: white, width: '100%' }} stickyHeader>
        <TableHead sx={{ backgroundColor: white }}>
          <TableRow>
            <TableCell
              size="small"
              sx={{
                whiteSpace: 'normal',
                backgroundColor: white,
                width: '20%',
              }}
            >
              <Tooltip
                arrow
                placement="right"
                title="ユーザーを特定するために必要な情報です。サポートへお問い合わせの際にお伝えください。"
              >
                <Typography
                  variant="body2"
                  component="span"
                  fontWeight="bold"
                  alignItems="center"
                  display="inline-flex"
                  gap={0.5}
                >
                  ユーザー管理番号 <InfoOutline fontSize="small" />
                </Typography>
              </Tooltip>
            </TableCell>
            <TableCell
              size="small"
              sx={{
                whiteSpace: 'normal',
                backgroundColor: white,
                width: '25%',
              }}
            >
              表示名
            </TableCell>
            <TableCell
              size="small"
              sx={{
                whiteSpace: 'normal',
                backgroundColor: white,
                width: '15%',
              }}
            >
              ロール（役割）
            </TableCell>
            <TableCell
              size="small"
              sx={{
                whiteSpace: 'normal',
                backgroundColor: white,
                width: '20%',
              }}
            >
              ユーザーグループ名
            </TableCell>
            <TableCell
              size="small"
              sx={{
                whiteSpace: 'normal',
                backgroundColor: white,
                width: '30%',
              }}
            >
              現場名
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {props.users.map((user: User) => (
            <TableRow
              onClick={() => openEditUser(user)}
              key={user.uuid}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: colors.action.hover,
                },
              }}
            >
              <TableCell
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                <Typography variant="body2">
                  {user.rumpCustomerMemberId}
                </Typography>
              </TableCell>

              <TableCell
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                <Typography variant="body2">{user.name}</Typography>
              </TableCell>

              <TableCell
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                <Typography variant="body2">
                  {user.role?.role ? roleLabels[user.role?.role] : ''}
                </Typography>
              </TableCell>

              <TableCell
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                <Typography variant="body2">
                  {formatUserGroupNames(user.userGroups)}
                </Typography>
              </TableCell>

              <TableCell
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                <Typography variant="body2">
                  {formatPlaceNodeNames(user.placeNodes, nodes)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default UserList
